/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useParams } from 'js/lib/useRouter';

import { Button, Divider, Typography2, breakpoints } from '@coursera/cds-core';

import type { SkillMasteryLevelFragment } from 'bundles/levelsets/levelsets-v2/api/__generated__/SkillMasteryLevelsForCurrentUser';
import { getSkillsMasteryLevelLabels } from 'bundles/levelsets/levelsets-v2/constants';
import type { Skill } from 'bundles/levelsets/levelsets-v2/types';
import { getLatestMasteryProgress } from 'bundles/levelsets/levelsets-v2/utils/formatConversion';

import _t from 'i18n!nls/levelsets';

type MasteryLevel = SkillMasteryLevelFragment['masteryLevel'];

type Props = {
  skill: Skill;
  masteryLevel?: MasteryLevel;
  masteryPercent?: number;
};

// If the background image is in a separate file, cards will appear with a white background and eventually get the
// background image when the browser decides to fetch it. By including it here it's compressed with the JS and the card
// will appear with the background image immediately.
const backgroundSvg = `<svg width="121" height="131" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M137.442 11.676 68.721-28 0 11.676v79.352l68.721 39.676 68.721-39.676V11.676Zm-18.957 10.947L68.722-6.108l-49.764 28.73v57.463l49.764 28.73 49.763-28.73V22.623Z" fill="#F0F6FF"/><path d="m68.532 15.283 11.025 20.895 23.279 4.028L86.37 57.148l3.363 23.385-21.2-10.425L47.33 80.533l3.363-23.385-16.466-16.942 23.28-4.028 11.024-20.895Z" fill="#F0F6FF"/></svg>`;

const styles = {
  card: css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-width: 312px;
    min-height: 148px;
    padding: var(--cds-spacing-300) var(--cds-spacing-300) 20px var(--cds-spacing-300);
    border-radius: 16px;
    border: 2px solid var(--cds-color-grey-50);
    background-color: var(--cds-color-neutral-background-primary);
    background-image: url(data:image/svg+xml;base64,${btoa(backgroundSvg)});
    background-repeat: no-repeat;
    background-position: right 0 top 0;

    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-200) var(--cds-spacing-150) var(--cds-spacing-100) var(--cds-spacing-150);
    }
  `,
  skillName: css`
    ${breakpoints.down('sm')} {
      font-size: 16px;
    }
  `,
  actionLinksNoMastery: css`
    margin-top: var(--cds-spacing-300);
    padding: var(--cds-spacing-100) 0;
    display: flex;
    align-items: flex-start;
    align-content: flex-start;
    gap: var(--cds-spacing-300);
    flex-wrap: wrap;
  `,
  actionLinkPrimary: css`
    padding: 0;

    &:hover {
      text-decoration: none;
    }
  `,
  actionLinkSecondary: css`
    padding: 0;

    &:hover {
      text-decoration: none;
      background-color: transparent;
    }
  `,
  divider: css`
    margin-top: var(--cds-spacing-200);
  `,
  actionLinksWithMastery: css`
    margin-top: var(--cds-spacing-150);
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  `,
  actionLinkUnderlined: css`
    text-decoration: underline;
    font-weight: 400;
    padding: 0;
    color: var(--cds-color-neutral-primary);

    &:hover {
      background-color: transparent;
    }
  `,
  currentMasteryContainer: css`
    margin-top: var(--cds-spacing-300);
    display: flex;
    gap: var(--cds-spacing-150);
  `,
};

export const LevelSetCard = ({ skill, masteryLevel, masteryPercent }: Props) => {
  const { programSlug } = useParams();

  const attemptUrl = `/levelsets/assessment/${skill.skillId}?source=v2card&programSlug=${programSlug}`;
  const recommendationsUrl = `/programs/${programSlug}/skills/${skill.skillId}?source=levelset2_card&mastery=true`;

  const { attainedMasteryLevel } = getLatestMasteryProgress(masteryLevel, masteryPercent);

  const masteryLevelLabels = getSkillsMasteryLevelLabels();
  const MasteryLevelIcon = attainedMasteryLevel
    ? masteryLevelLabels[attainedMasteryLevel].icon
    : () => <React.Fragment />;

  return (
    <div css={styles.card}>
      <div>
        <Typography2 variant="subtitleLarge" component="h2" css={styles.skillName}>
          {skill.skillName}
        </Typography2>
        {attainedMasteryLevel && (
          <div css={styles.currentMasteryContainer}>
            <div>
              <MasteryLevelIcon />
            </div>
            <div>
              <Typography2 component="p" variant="bodySecondary">
                {masteryLevelLabels[attainedMasteryLevel].name}
              </Typography2>
              <Typography2 component="p" variant="subtitleMedium">
                {masteryLevelLabels[attainedMasteryLevel].description}
              </Typography2>
            </div>
          </div>
        )}
      </div>

      {!attainedMasteryLevel && (
        <div css={styles.actionLinksNoMastery}>
          <div>
            <Button variant="primary" size="small" component="a" href={attemptUrl}>
              {_t('Evaluate')}
            </Button>
          </div>
          <div>
            <Button
              variant="ghost"
              size="small"
              component="a"
              href={recommendationsUrl}
              css={styles.actionLinkSecondary}
            >
              {_t('View recommendations')}
            </Button>
          </div>
        </div>
      )}

      {attainedMasteryLevel && (
        <React.Fragment>
          <Divider color="light" css={styles.divider} />
          <div css={styles.actionLinksWithMastery}>
            <div>
              <Button
                variant="ghost"
                size="small"
                component="a"
                href={recommendationsUrl}
                css={styles.actionLinkSecondary}
              >
                {_t('View recommendations')}
              </Button>
            </div>
            <div>
              <Button variant="ghost" size="small" component="a" href={attemptUrl} css={styles.actionLinkUnderlined}>
                {_t('Re-attempt')}
              </Button>
            </div>
          </div>
        </React.Fragment>
      )}
    </div>
  );
};
